import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CategoryIcon from '@mui/icons-material/Category';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment-jalaali';
import workAssignmentService, {
  MyTaskItem,
  MyTasksResponse,
  WorkCategoryCode,
  AdminShiftStatus,
  ShiftWindow,
  CATEGORY_LABELS,
  STATUS_LABELS,
  DAYS_OF_WEEK_FA,
  formatMinuteFa,
} from '../services/workAssignment.service';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import './EntityManagement.css';

/**
 * «کارهای من» — every work item assigned to the logged-in admin, in one list.
 *
 * Assignment is a cross-cutting layer over ~17 unrelated models, so this page
 * is the only place an admin can see their whole queue without walking every
 * module. Data comes from GET /admin/work-assignment/my-tasks, which is
 * self-scoped server-side (no userId param exists) — hence no permission gate.
 */

const fa = (v: string | number): string => toPersianDigits(String(v));

const formatDateTime = (date?: string | null): string => {
  if (!date) return '—';
  try {
    return fa(moment(date).locale('fa').format('jYYYY/jMM/jDD — HH:mm'));
  } catch {
    return '—';
  }
};

/** Human age: minutes under an hour, hours under a day, days beyond. */
const formatAge = (hours: number): string => {
  if (!hours || hours < 1) return `${fa(Math.max(1, Math.round(hours * 60)))} دقیقه`;
  if (hours < 24) return `${fa(Math.round(hours))} ساعت`;
  return `${fa(Math.round(hours / 24))} روز`;
};

/** Raw domain statuses across all models → Persian. Unknown values pass through. */
const STATUS_FA: Record<string, { label: string; tag: string }> = {
  pending: { label: 'در انتظار بررسی', tag: 'tag-warning' },
  pending_review: { label: 'در انتظار بررسی', tag: 'tag-warning' },
  pending_approval: { label: 'در انتظار تایید', tag: 'tag-warning' },
  open: { label: 'باز', tag: 'tag-warning' },
  in_progress: { label: 'در حال بررسی', tag: 'tag-info' },
  waiting_for_response: { label: 'در انتظار پاسخ', tag: 'tag-info' },
  approved: { label: 'تایید شده', tag: 'tag-success' },
  verified: { label: 'تایید شده', tag: 'tag-success' },
  resolved: { label: 'حل شده', tag: 'tag-success' },
  closed: { label: 'بسته شده', tag: 'tag-neutral' },
  rejected: { label: 'رد شده', tag: 'tag-danger' },
  waived: { label: 'بخشوده', tag: 'tag-neutral' },
  dispute: { label: 'در حال داوری', tag: 'tag-warning' },
  active: { label: 'فعال', tag: 'tag-info' },
};

const statusView = (status: string, isOpen: boolean) =>
  STATUS_FA[status] || { label: status || (isOpen ? 'باز' : 'بسته'), tag: isOpen ? 'tag-warning' : 'tag-neutral' };

const formatAmount = (n?: number): string =>
  typeof n === 'number' ? `${fa(n.toLocaleString('en-US'))} تومان` : '—';

const EMPTY: MyTasksResponse = {
  items: [],
  meta: { page: 1, limit: 20, totalItems: 0, totalPages: 1 },
  counts: [],
  summary: { open: 0, total: 0, stale: 0, categories: 0 },
  myCategories: [],
  truncated: [],
};

const MyTasks: React.FC = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<MyTasksResponse>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<'open' | 'all'>('open');
  const [categoryFilter, setCategoryFilter] = useState<WorkCategoryCode | ''>('');
  const [sort, setSort] = useState<'oldest' | 'newest'>('oldest');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [shiftStatus, setShiftStatus] = useState<AdminShiftStatus | null>(null);
  const [shiftWindow, setShiftWindow] = useState<ShiftWindow>({});

  /**
   * Counts + summary from the last UNFILTERED fetch. The server scopes `counts`
   * to the category filter, so reading chips straight off `data` would collapse
   * them to the selected category and strand the user (no way to switch from
   * category A to B without clearing first). Keeping the full set here means the
   * chip row stays navigable.
   */
  const [allCounts, setAllCounts] = useState<MyTasksResponse['counts']>([]);
  const [allSummary, setAllSummary] = useState<MyTasksResponse['summary']>(EMPTY.summary);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await workAssignmentService.getMyTasks({
        status: statusFilter,
        category: categoryFilter,
        sort,
        page,
        limit: rowsPerPage,
      });
      setData(res || EMPTY);
      if (!categoryFilter && res) {
        setAllCounts(res.counts);
        setAllSummary(res.summary);
      }
    } catch (err: any) {
      console.error('[MyTasks] load failed', err);
      setError(err?.response?.data?.message || 'خطا در بارگذاری کارهای اختصاص‌یافته');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, sort, page, rowsPerPage]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Shift strip is decorative context — a failure here must not block the list.
  useEffect(() => {
    let alive = true;
    workAssignmentService
      .getMyStatus()
      .then((res) => {
        if (!alive) return;
        setShiftStatus(res.liveStatus);
        setShiftWindow(res.shiftWindow || {});
      })
      .catch(() => {
        /* noop */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Any filter change invalidates the current page number.
  const changeStatus = (v: 'open' | 'all') => {
    setStatusFilter(v);
    setPage(1);
  };
  const changeCategory = (v: WorkCategoryCode | '') => {
    setCategoryFilter(v);
    setPage(1);
  };
  const changeSort = (v: 'oldest' | 'newest') => {
    setSort(v);
    setPage(1);
  };

  const hasActiveFilters = statusFilter !== 'open' || categoryFilter !== '' || sort !== 'oldest';
  const clearFilters = () => {
    setStatusFilter('open');
    setCategoryFilter('');
    setSort('oldest');
    setPage(1);
  };

  const { items, meta, truncated } = data;
  // Stat tiles and chips describe the WHOLE queue, not the filtered slice —
  // otherwise selecting a category would make "کارهای باز" read as that
  // category's count and hide the rest of the workload.
  const summary = allSummary;
  const counts = allCounts;
  const totalPages = meta.totalPages || 1;
  const startItem = meta.totalItems === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const endItem = Math.min(meta.page * meta.limit, meta.totalItems);

  // Category chips are built from the counts the server returned for the CURRENT
  // status filter, so the numbers always match the list below them.
  const chips = useMemo(
    () =>
      counts.map((c) => ({
        code: c.category,
        label: CATEGORY_LABELS[c.category] || c.nameFa,
        count: statusFilter === 'open' ? c.open : c.total,
      })),
    [counts, statusFilter]
  );

  const shiftInfo = shiftStatus ? STATUS_LABELS[shiftStatus] : null;

  const openTask = (t: MyTaskItem) => {
    if (!t.link || t.link === '#') return;
    navigate(t.link);
  };

  return (
    <div className="entity-page">
      {/* ---------- Header ---------- */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">کارهای من</h1>
          <p className="entity-subtitle">
            همهٔ مواردی که در حوزه‌های کاری مختلف به شما اختصاص یافته است — یکجا.
          </p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={loadTasks} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* ---------- Shift context strip ---------- */}
      {shiftStatus && shiftStatus !== 'no_profile' && shiftInfo && (
        <div
          style={{
            direction: 'rtl',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 10,
            background: shiftStatus === 'on_shift' ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)',
            border: `1px solid ${shiftStatus === 'on_shift' ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}`,
            borderInlineStart: `4px solid ${shiftStatus === 'on_shift' ? '#16a34a' : '#d97706'}`,
            fontSize: 12.5,
            color: shiftStatus === 'on_shift' ? '#166534' : '#78350f',
          }}
        >
          <ScheduleIcon sx={{ fontSize: 18 }} />
          <strong>وضعیت شیفت: {shiftInfo.label}</strong>
          {shiftStatus === 'on_shift' && shiftWindow.current && (
            <span style={{ opacity: 0.9 }}>
              شیفت فعلی: {DAYS_OF_WEEK_FA[shiftWindow.current.dayOfWeek]}،{' '}
              {formatMinuteFa(shiftWindow.current.startMinute)} تا {formatMinuteFa(shiftWindow.current.endMinute)}
            </span>
          )}
          {shiftStatus !== 'on_shift' && shiftWindow.next && (
            <span style={{ opacity: 0.9 }}>
              شیفت بعدی: {DAYS_OF_WEEK_FA[shiftWindow.next.dayOfWeek]} ساعت{' '}
              {formatMinuteFa(shiftWindow.next.startMinute)}
              {shiftWindow.next.daysAhead > 0 ? ` (${fa(shiftWindow.next.daysAhead)} روز بعد)` : ' (همین امروز)'}
            </span>
          )}
        </div>
      )}

      {shiftStatus === 'no_profile' && (
        <div
          style={{
            direction: 'rtl',
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 10,
            background: 'rgba(178, 83, 115,0.08)',
            border: '1px solid rgba(178, 83, 115,0.25)',
            fontSize: 12.5,
            color: '#8E3D59',
          }}
        >
          پروفایل کاری برای شما تعریف نشده است؛ کارها فقط در صورت اختصاص دستی اینجا نمایش داده می‌شوند.
        </div>
      )}

      {error && (
        <div className="tag tag-sm tag-danger" style={{ marginBottom: 16, display: 'block', padding: '10px 14px' }}>
          {error}
        </div>
      )}

      {truncated.length > 0 && (
        <div
          style={{
            direction: 'rtl',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 10,
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.25)',
            fontSize: 12.5,
            color: '#78350f',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18 }} />
          <span>
            تعداد موارد در حوزه‌های {truncated.map((c) => CATEGORY_LABELS[c] || c).join('، ')} از سقف نمایش بیشتر است؛
            این فهرست کامل نیست.
          </span>
        </div>
      )}

      {/* ---------- Stats ---------- */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange">
            <AssignmentIcon />
          </div>
          <div>
            <p className="entity-stat-label">کارهای باز</p>
            <p className="entity-stat-value">{fa(summary.open)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red">
            <WarningAmberIcon />
          </div>
          <div>
            <p className="entity-stat-label">معطل‌مانده (بیش از ۴۸ ساعت)</p>
            <p className="entity-stat-value">{fa(summary.stale)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue">
            <CategoryIcon />
          </div>
          <div>
            <p className="entity-stat-label">حوزه‌های درگیر</p>
            <p className="entity-stat-value">{fa(summary.categories)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon purple">
            <InboxIcon />
          </div>
          <div>
            <p className="entity-stat-label">مجموع موارد</p>
            <p className="entity-stat-value">{fa(summary.total)}</p>
          </div>
        </div>
      </div>

      {/* ---------- Category chips ---------- */}
      {chips.length > 0 && (
        <div
          style={{
            direction: 'rtl',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <button
            type="button"
            className={`tag tag-sm ${categoryFilter === '' ? 'tag-info' : 'tag-neutral'}`}
            style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}
            onClick={() => changeCategory('')}
          >
            همه ({fa(statusFilter === 'open' ? summary.open : summary.total)})
          </button>
          {chips.map((c) => (
            <button
              key={c.code}
              type="button"
              className={`tag tag-sm ${categoryFilter === c.code ? 'tag-info' : 'tag-neutral'}`}
              style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}
              onClick={() => changeCategory(c.code)}
            >
              {c.label} ({fa(c.count)})
            </button>
          ))}
        </div>
      )}

      {/* ---------- Filters ---------- */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <FilterAltIcon fontSize="small" /> وضعیت
            </label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => changeStatus(e.target.value as 'open' | 'all')}
            >
              <option value="open">فقط کارهای باز</option>
              <option value="all">همه (شامل بسته‌شده‌ها)</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">
              <CategoryIcon fontSize="small" /> حوزه کاری
            </label>
            <select
              className="filter-field"
              value={categoryFilter}
              onChange={(e) => changeCategory(e.target.value as WorkCategoryCode | '')}
            >
              <option value="">همه حوزه‌ها</option>
              {chips.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">
              <SortIcon fontSize="small" /> ترتیب
            </label>
            <select
              className="filter-field"
              value={sort}
              onChange={(e) => changeSort(e.target.value as 'oldest' | 'newest')}
            >
              <option value="oldest">قدیمی‌ترین اول (صف کاری)</option>
              <option value="newest">جدیدترین اول</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک کردن فیلترها
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---------- List ---------- */}
      <div className="entity-table-wrapper">
        {loading && items.length === 0 ? (
          <div className="entity-loading">
            <CircularProgress size={32} />
          </div>
        ) : items.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon">
              <InboxIcon style={{ fontSize: 48 }} />
            </div>
            <p className="entity-empty-text">
              {statusFilter === 'open'
                ? 'در حال حاضر هیچ کار فعالی به شما اختصاص داده نشده است.'
                : 'موردی یافت نشد.'}
            </p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>عنوان</th>
                  <th>حوزه کاری</th>
                  <th>وضعیت</th>
                  <th>مبلغ</th>
                  <th>زمان اختصاص</th>
                  <th>سن</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const sv = statusView(t.status, t.isOpen);
                  return (
                    <tr key={`${t.category}-${t.id}`}>
                      <td>
                        <div className="entity-info">
                          <span className="entity-name">{t.title}</span>
                          {t.subtitle && <span className="entity-desc">{t.subtitle}</span>}
                        </div>
                      </td>
                      <td>
                        <span className="tag tag-sm tag-neutral">
                          {CATEGORY_LABELS[t.category] || t.categoryNameFa}
                        </span>
                      </td>
                      <td>
                        <span className={`tag tag-sm ${sv.tag}`}>{sv.label}</span>
                      </td>
                      <td>{formatAmount(t.amount)}</td>
                      <td>{formatDateTime(t.assignedAt || t.createdAt)}</td>
                      <td>
                        {t.isStale ? (
                          <Tooltip title="بیش از ۴۸ ساعت است که باز مانده">
                            <span className="tag tag-sm tag-danger">{formatAge(t.ageHours)}</span>
                          </Tooltip>
                        ) : (
                          <span>{formatAge(t.ageHours)}</span>
                        )}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <Tooltip title="باز کردن">
                            <button
                              className="action-btn"
                              onClick={() => openTask(t)}
                              disabled={!t.link || t.link === '#'}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="entity-cards">
              {items.map((t) => {
                const sv = statusView(t.status, t.isOpen);
                return (
                  <div className="entity-card" key={`card-${t.category}-${t.id}`}>
                    <div className="entity-card-header">
                      <div className="entity-card-info">
                        <span className="entity-card-name">{t.title}</span>
                        <span className="entity-card-desc">
                          {CATEGORY_LABELS[t.category] || t.categoryNameFa}
                          {t.subtitle ? ` — ${t.subtitle}` : ''}
                        </span>
                      </div>
                      <div className="entity-card-status">
                        <span className={`tag tag-sm ${sv.tag}`}>{sv.label}</span>
                      </div>
                    </div>
                    <div className="entity-card-desc" style={{ marginBottom: 8 }}>
                      اختصاص: {formatDateTime(t.assignedAt || t.createdAt)} — سن: {formatAge(t.ageHours)}
                      {t.isStale ? ' (معطل‌مانده)' : ''}
                      {typeof t.amount === 'number' ? ` — ${formatAmount(t.amount)}` : ''}
                    </div>
                    <div className="entity-card-actions">
                      <button
                        className="entity-card-btn primary"
                        onClick={() => openTask(t)}
                        disabled={!t.link || t.link === '#'}
                      >
                        باز کردن
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {fa(startItem)} تا {fa(endItem)} از {fa(meta.totalItems)} مورد
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>۱۰</option>
                  <option value={20}>۲۰</option>
                  <option value={50}>۵۰</option>
                  <option value={100}>۱۰۰</option>
                </select>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span>
                  {fa(page)} / {fa(totalPages)}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyTasks;
